import { createReducer, createActions } from 'reduxsauce';
import { FREQUENCIES, SERVICE_TYPES, BOOKING_STAGE } from '@services/minte';
import get from 'lodash-es/get';

const { Types, Creators } = createActions({
  resetBooking: ['isSoft'],
  resetBookingWithState: ['data'],
  initBookingFromClean: ['clean', 'step', 'initialChanges', 'isRecurring'],
  initMoveOutBooking: ['isFromAccount'],
  setIsForPropertyManager: ['isPMBooking'],
  fetchBookingsData: null,
  setBookingId: ['bookingId'],
  setBookingStep: ['step'],
  setBookingRecipient: ['recipient'],
  setBookingFrequency: ['frequency'],
  setBookingProperty: ['property'],
  setBookingDefaultProperty: ['property'],
  setBookingServiceType: ['serviceType'],
  addBookingAddon: ['addon'],
  removeBookingAddon: ['addon'],
  setBookingServiceDate: ['cleanDate'],
  setAvailableDates: ['availableDates'],
  setAvailableDatesLoading: ['datesLoading'],
  goToNextBookingStep: ['data'],
  goToPrevBookingStep: ['data']
});

export const BookingTypes = Types;
export default Creators;

/* ------- Initial State --------- */
export const INITIAL_STATE = {
  bookingId: null,
  prevStep: '',
  step: BOOKING_STAGE.FREQUENCY,
  frequency: '',
  serviceType: '',
  addons: [],
  cleanDate: {},
  availableDates: [],
  timeframe: 'anytime',
  recipient: null,
  isPMBooking: false,
  datesLoading: false,
  isFromAccount: false,
  property: null
};

/* ------- Selectors --------- */
export const BookingSelectors = {
  selectExistingBookingId: state => state.booking.bookingId,
  selectCurrentProperty: state => state.booking.property,
  selectCurrentPropertyId: state => get(state, 'booking.property.propertyID'),
  selectCurrentStep: state => state.booking.step,
  selectFrequency: state => state.booking.frequency,
  selectServiceType: state => state.booking.serviceType,
  selectServiceDate: state => state.booking.cleanDate,
  selectAddons: state => state.booking.addons,
  selectAvailableDates: state => state.booking.availableDates,
  selectDatesLoading: state => state.booking.datesLoading,
  selectRecipientInfo: state => state.booking.recipient,
  selectIsFromAccount: state => state.booking.isFromAccount
};

/* -------- Reducers ---------- */
export const setStep = (state, { step }) => {
  return {
    ...state,
    prevStep: state.step,
    step
  };
};

export const setRecipient = (state, { recipient }) => {
  return {
    ...state,
    recipient
  };
};

export const setBookingId = (state, { bookingId }) => {
  return {
    ...state,
    bookingId
  };
};

export const setFrequency = (state, { frequency }) => {
  return {
    ...state,
    frequency
  };
};

export const setBookingProperty = (state, { property }) => {
  return {
    ...state,
    property
  };
};

export const setServiceType = (state, { serviceType }) => {
  return {
    ...state,
    serviceType
  };
};

export const setServiceDate = (state, { cleanDate }) => {
  return {
    ...state,
    cleanDate
  };
};

export const addAddon = (state, { addon }) => {
  if (Array.isArray(addon)) {
    return {
      ...state,
      addons: [...state.addons, ...addon]
    };
  }
  return {
    ...state,
    addons: [...state.addons, addon]
  };
};

export const removeAddon = (state, { addon }) => {
  return {
    ...state,
    addons: state.addons.filter(item => item !== addon)
  };
};

export const setAvailableDates = (state, { availableDates }) => {
  return {
    ...state,
    availableDates
  };
};

export const setAvailableDatesLoading = (state, { datesLoading }) => {
  return {
    ...state,
    datesLoading
  };
};

export const resetBooking = (state, { isSoft }) => {
  const { isPMBooking, availableDates, property } = state;
  const newState = {
    ...INITIAL_STATE
  };

  if (isSoft) {
    newState.availableDates = availableDates;
    newState.property = property;
  }

  if (isPMBooking) {
    newState.isPMBooking = true;
    newState.step = BOOKING_STAGE.RECIPIENT;
  }

  return newState;
};

export const setIsForPropertyManager = (state, { isPMBooking }) => {
  return {
    ...state,
    isPMBooking
  };
};

export const resetBookingWithState = (state, { data }) => {
  const { isPMBooking } = state;
  const newState = {
    ...INITIAL_STATE,
    ...data
  };

  if (isPMBooking) {
    newState.isPMBooking = true;
    newState.step = BOOKING_STAGE.RECIPIENT;
  }

  return newState;
};

const initMoveOutBooking = (state, { isFromAccount }) => {
  const { availableDates, property } = state;
  return {
    ...INITIAL_STATE,
    isFromAccount,
    availableDates,
    frequency: FREQUENCIES.ONCE,
    step: BOOKING_STAGE.SERVICE_DATE,
    serviceType: SERVICE_TYPES.MOVE_OUT,
    property
  };
};

const setBookingDefaultProperty = (state, { property }) => {
  const { property: existingProperty } = state;

  if (existingProperty) {
    return state;
  }

  return {
    ...state,
    property
  };
};

/* -------- Hookup Reducers to Types -------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.RESET_BOOKING]: resetBooking,
  [Types.RESET_BOOKING_WITH_STATE]: resetBookingWithState,
  [Types.SET_BOOKING_ID]: setBookingId,
  [Types.SET_BOOKING_STEP]: setStep,
  [Types.SET_BOOKING_RECIPIENT]: setRecipient,
  [Types.SET_BOOKING_FREQUENCY]: setFrequency,
  [Types.SET_BOOKING_PROPERTY]: setBookingProperty,
  [Types.SET_BOOKING_DEFAULT_PROPERTY]: setBookingDefaultProperty,
  [Types.SET_BOOKING_SERVICE_TYPE]: setServiceType,
  [Types.ADD_BOOKING_ADDON]: addAddon,
  [Types.REMOVE_BOOKING_ADDON]: removeAddon,
  [Types.SET_BOOKING_SERVICE_DATE]: setServiceDate,
  [Types.SET_AVAILABLE_DATES]: setAvailableDates,
  [Types.SET_AVAILABLE_DATES_LOADING]: setAvailableDatesLoading,
  [Types.SET_IS_FOR_PROPERTY_MANAGER]: setIsForPropertyManager,
  [Types.INIT_MOVE_OUT_BOOKING]: initMoveOutBooking
});
